/**
 * Helper functions to manage authentication tokens in sessionStorage
 * This allows each browser tab to have its own independent session
 */

import { authenticateSettings } from 'components/helper/appConfig';
import { setSentryUser } from '../sentry';

/**
 * Get access token from sessionStorage
 */
export const getAccessToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem(authenticateSettings.tokenName) || undefined;
};

/**
 * Set access token in sessionStorage
 */
export const setAccessToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(authenticateSettings.tokenName, token);
};

/**
 * Remove access token from sessionStorage
 */
export const removeAccessToken = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(authenticateSettings.tokenName);
};

/**
 * Get userId from sessionStorage
 */
export const getUserId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem('userId') || undefined;
};

/**
 * Set userId in sessionStorage
 */
export const setUserId = (userId: string | number): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem('userId', String(userId));
  setSentryUser(userId);
};

/**
 * Remove userId from sessionStorage
 */
export const removeUserId = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('userId');
};

/**
 * Get user role from sessionStorage
 */
export const getUserRole = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem('userRole') || undefined;
};

/**
 * Get current user email from sessionStorage
 */
export const getCurrentUserEmail = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem('currentUserEmail') || undefined;
};

/**
 * Get is_confirmed_by_phone from sessionStorage
 */
export const getIsConfirmedByPhone = (): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem('is_confirmed_by_phone') === 'true';
};

/**
 * Set is_confirmed_by_phone in sessionStorage
 */
export const setIsConfirmedByPhone = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem('is_confirmed_by_phone', String(value));
};

/**
 * Remove is_confirmed_by_phone from sessionStorage
 */
export const removeIsConfirmedByPhone = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('is_confirmed_by_phone');
};
/**
 * Clear all authentication data from sessionStorage and localStorage
 */
export const clearAuthData = (): void => {
  setSentryUser();
  // Clear sessionStorage
  removeAccessToken();
  removeUserId();
  sessionStorage.removeItem('currentUserEmail');
  sessionStorage.removeItem('userRole');
  sessionStorage.removeItem('session_student_id');
  sessionStorage.removeItem('isDashBoardLoaded');
  sessionStorage.removeItem('expireTime');
  sessionStorage.removeItem('refresh_token');
  removeIsConfirmedByPhone();
  
  // Clear localStorage authentication-related data
  if (typeof window !== 'undefined') {
    localStorage.removeItem('currentUserEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('isDashBoardLoaded');
    localStorage.removeItem('expireTime');
    localStorage.removeItem('LAST_ACCESS_TOKEN');
  }
};
